import React, { useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { BookOpen, AlertCircle, Loader2, Eye, EyeOff } from 'lucide-react';
import { trackFormStart } from '../utils/analytics';
import { RequestAccessModal } from './RequestAccessModal';
import { ForgotPasswordModal } from './ForgotPasswordModal';

interface AuthPageProps {
  onLogin: (email: string, password: string) => Promise<{ success: boolean; message?: string }>;
  onRegister?: (email: string, password: string, fullName: string, company: string) => Promise<{ success: boolean; message?: string }>;
}

export function AuthPage({ onLogin, onRegister }: AuthPageProps) {
  const [loading, setLoading] = useState(false);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [fullName, setFullName] = useState('');
  const [company, setCompany] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isRegisterMode, setIsRegisterMode] = useState(false);
  const [showFreeCourseModal, setShowFreeCourseModal] = useState(false);
  const [showForgotPasswordModal, setShowForgotPasswordModal] = useState(false);
  const formStartTracked = useRef(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    // Basic validation
    if (!email || !password) {
      setError('Email and password are required');
      setLoading(false);
      return;
    }

    if (isRegisterMode && (!fullName || !company)) {
      setError('Full name and company are required for registration');
      setLoading(false);
      return;
    }

    if (!email.includes('@')) {
      setError('Please enter a valid email address');
      setLoading(false);
      return;
    }

    try {
      let result;
      if (isRegisterMode && onRegister) {
        result = await onRegister(email, password, fullName, company);
      } else {
        result = await onLogin(email, password);
      }
      
      if (!result.success) {
        setError(result.message || (isRegisterMode ? 'Registration failed' : 'Login failed'));
      }
      // If successful, App.tsx will handle the redirect
    } catch (error) {
      console.error(isRegisterMode ? 'Registration error:' : 'Login error:', error);
      setError('An unexpected error occurred. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="auth-page">
      <div className="auth-container">
        <div className="auth-card">
          <div className="auth-header">
            <div className="auth-logo">
              <BookOpen className="logo-icon" />
              <h1>recberry</h1>
            </div>
            <h2>{isRegisterMode ? 'Create your account' : 'Sign in to your account'}</h2>
            <p>{isRegisterMode ? 'Register with your credentials received via email' : 'Access your Principal IT Talent Training course'}</p>
          </div>

          {error && (
            <div className="auth-alert error">
              <AlertCircle />
              <span>{error}</span>
            </div>
          )}

          <form className="auth-form" onSubmit={handleSubmit}>
            <div className="form-fields">
              {isRegisterMode && (
                <>
                  <div className="form-field">
                    <label htmlFor="fullName">Full Name</label>
                    <input
                      id="fullName"
                      name="fullName"
                      type="text"
                      required
                      value={fullName}
                      onChange={(e) => setFullName(e.target.value)}
                      onFocus={() => {
                        if (!formStartTracked.current) {
                          formStartTracked.current = true;
                          trackFormStart('auth_register');
                        }
                      }}
                      placeholder="Enter your full name"
                      disabled={loading}
                    />
                  </div>
                  
                  <div className="form-field">
                    <label htmlFor="company">Company</label>
                    <input
                      id="company"
                      name="company"
                      type="text"
                      required
                      value={company}
                      onChange={(e) => setCompany(e.target.value)}
                      placeholder="Enter your company name"
                      disabled={loading}
                    />
                  </div>
                </>
              )}
              
              <div className="form-field">
                <label htmlFor="email">Email address</label>
                <input
                  id="email"
                  name="email"
                  type="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  onFocus={() => {
                    if (!formStartTracked.current) {
                      formStartTracked.current = true;
                      trackFormStart('auth_login');
                    }
                  }}
                  placeholder="Enter your email"
                  disabled={loading}
                />
              </div>
              
              <div className="form-field">
                <label htmlFor="password">Password</label>
                <div style={{ position: 'relative' }}>
                  <input
                    id="password"
                    name="password"
                    type={showPassword ? 'text' : 'password'}
                    required
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="Enter your password"
                    disabled={loading}
                    style={{ paddingRight: '3rem' }}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    disabled={loading}
                    style={{
                      position: 'absolute',
                      right: '1rem',
                      top: '50%',
                      transform: 'translateY(-50%)',
                      background: 'none',
                      border: 'none',
                      color: 'var(--gray)',
                      cursor: 'pointer',
                      padding: '0.25rem'
                    }}
                  >
                    {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                  </button>
                </div>
                <button
                  type="button"
                  onClick={() => setShowForgotPasswordModal(true)}
                  style={{
                    marginTop: '0.5rem',
                    background: 'none',
                    border: 'none',
                    color: 'var(--gray)',
                    fontSize: '0.8rem',
                    textAlign: 'right',
                    cursor: 'pointer'
                  }}
                >
                  Forgot password?
                </button>
              </div>
            </div>

            <button
              type="submit"
              disabled={loading}
              className="auth-button"
            >
              {loading ? (
                <>
                  <Loader2 className="button-icon spinning" />
                  {isRegisterMode ? 'Creating account...' : 'Signing in...'}
                </>
              ) : (
                isRegisterMode ? 'Create Account' : 'Sign In'
              )}
            </button>
          </form>

          <div style={{ 
            textAlign: 'center', 
            marginTop: '2rem', 
            paddingTop: '2rem', 
            borderTop: '1px solid #eee',
            color: 'var(--gray)',
            fontSize: '0.875rem'
          }}>
            {isRegisterMode ? (
              <>
                <p>
                  Already have an account? Sign in instead.
                </p>
                <button
                  onClick={() => setIsRegisterMode(false)}
                  style={{ 
                    color: 'var(--orange)', 
                    textDecoration: 'none',
                    fontWeight: 500,
                    marginTop: '0.5rem',
                    display: 'inline-block',
                    background: 'none',
                    border: 'none',
                    cursor: 'pointer',
                    fontSize: '0.875rem'
                  }}
                >
                  Sign In
                </button>
              </>
            ) : (
              <>
                <p>
                  Don't have an account? Request access to the free course.
                </p>
                <button
                  onClick={() => {
                    setShowFreeCourseModal(true);
                    setIsRegisterMode(false);
                  }}
                  style={{
                    color: 'var(--orange)',
                    textDecoration: 'none',
                    fontWeight: 500,
                    marginTop: '0.5rem',
                    display: 'inline-block',
                    background: 'none',
                    border: 'none',
                    cursor: 'pointer',
                    fontSize: '0.875rem'
                  }}
                >
                  Request Access
                </button>
              </>
            )}
            <br />
            <Link 
              to="/" 
              style={{ 
                color: 'var(--gray)', 
                textDecoration: 'none',
                fontWeight: 400,
                marginTop: '1rem',
                display: 'inline-block',
                fontSize: '0.8rem'
              }}
            >
              ← Back to Course Preview
            </Link>
          </div>
        </div>
      </div>

      <RequestAccessModal
        isOpen={showFreeCourseModal}
        onClose={() => setShowFreeCourseModal(false)}
        onSuccess={() => setShowFreeCourseModal(false)}
        title="Start Free Course"
      />
      <ForgotPasswordModal
        isOpen={showForgotPasswordModal}
        onClose={() => setShowForgotPasswordModal(false)}
      />
    </div>
  );
}

export default AuthPage;